import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import FicheCamionPage from "./FicheCamionPage";
import { useAuth } from "../context/AuthContext";
import { getFoodTruck, updateFoodTruck, ouvrirFichier, getSpecialties, uploadFile, addDocument, getMyCotisations, changePassword } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Store,
  User,
  Mail,
  Phone,
  MapPin,
  Camera,
  FileText,
  ChefHat,
  Save,
  Upload,
  X,
  Image,
  Truck,
  UtensilsCrossed,
  BookOpen,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Plus,
  Download,
  CreditCard,
  Building2,
  Lock,
  Eye,
  EyeOff,
  Globe,
  Bell,
  Settings,
  Utensils,
  Zap,
  Users,
  Car,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Catégories de photos
const PHOTO_CATEGORIES = [
  { id: "camion_exterieur", label: "Camion Extérieur", icon: Truck, description: "Photos de l'extérieur de votre food truck" },
  { id: "camion_interieur", label: "Camion Intérieur", icon: Store, description: "Photos de l'intérieur et de votre espace de travail" },
  { id: "menu", label: "Menu", icon: BookOpen, description: "Photos de votre carte et menus" },
  { id: "plats", label: "Plats", icon: UtensilsCrossed, description: "Photos de vos plats et spécialités" },
];

// Documents requis
const REQUIRED_DOCUMENTS = [
  { id: "kbis", label: "Extrait Kbis", hasExpiration: false, required: true },
  { id: "haccp", label: "Attestation HACCP", hasExpiration: false, required: true },
  { id: "identite", label: "Pièce d'identité", hasExpiration: true, required: true },
  { id: "assurance", label: "Assurance RC Pro", hasExpiration: true, required: true },
  { id: "carte_commercant", label: "Carte Commerçant Ambulant", hasExpiration: true, required: true },
  { id: "assurance_vehicule", label: "Assurance Véhicule", hasExpiration: true, required: true },
];

const toNum = (v) => { if (v === null || v === undefined || v === "") return null; const n = parseFloat(String(v).replace(",", ".")); return isNaN(n) ? null : n; };
const fmt = (n, u) => (n ? n.toFixed(2).replace(".", ",") + " " + u : "-");

export default function MonFoodTruckPage() {
  const { user } = useAuth();
  // Onglet actif piloté par ?tab= : permet d'arriver directement sur "documents"
  // depuis le bandeau « Deposer mes documents » (sinon on ouvre toujours "infos").
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "infos");
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setActiveTab(t);
  }, [searchParams]);
  const [foodTruck, setFoodTruck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [cotisationPaid, setCotisationPaid] = useState(false);
  const [cotisationStatus, setCotisationStatus] = useState(null);
  const [showAddDocDialog, setShowAddDocDialog] = useState(false);
  const [newDocument, setNewDocument] = useState({
    type: "autre",
    nom: "",
    description: "",
    date_expiration: "",
    file: null
  });
  const [uploadingNewDoc, setUploadingNewDoc] = useState(false);
  const fileInputRef = useRef({});
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (user?.food_truck_id) {
      loadData();
    } else if (user && !user.food_truck_id) {
      setLoading(false);
    }
  }, [user]);

  const loadData = async () => {
    if (!user?.food_truck_id) {
      setLoading(false);
      return;
    }
    
    try {
      const [ftResponse, specResponse, cotisationsRes, cotisationStatusRes] = await Promise.all([
        getFoodTruck(user.food_truck_id),
        getSpecialties(),
        getMyCotisations(),
        fetch(`${API_URL}/api/auth/cotisation-status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        }).then(res => res.json()).catch(() => null)
      ]);
      setFoodTruck(ftResponse.data);
      setSpecialties(specResponse.data.specialites || []);
      
      // Set cotisation status
      if (cotisationStatusRes) {
        setCotisationStatus(cotisationStatusRes);
        setCotisationPaid(cotisationStatusRes.payee);
      } else {
        // Fallback to old method
        const currentYear = new Date().getFullYear();
        const currentCotisation = cotisationsRes.data.find(c => c.annee === currentYear);
        setCotisationPaid(currentCotisation?.statut === "paye" || currentCotisation?.statut === "offert");
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateFoodTruck(foodTruck.id, {
        nom_food_truck: foodTruck.nom_food_truck,
        nom_contact: foodTruck.nom_contact,
        email: foodTruck.email,
        telephone: foodTruck.telephone,
        adresse: foodTruck.adresse,
        description: foodTruck.description,
        specialites: foodTruck.specialites,
        photos: foodTruck.photos,
        // Equipment & Team fields
        type_vehicule: foodTruck.type_vehicule,
        plaque_immatriculation: foodTruck.plaque_immatriculation,
        puissance_electrique: foodTruck.puissance_electrique,
        longueur: toNum(foodTruck.longueur),
        largeur: toNum(foodTruck.largeur),
        hauteur: toNum(foodTruck.hauteur),
        staff_base: foodTruck.staff_base,
        // Extended profile fields
        societe: foodTruck.societe,
        representant: foodTruck.representant,
        communications: foodTruck.communications,
        point_restauration: foodTruck.point_restauration,
        descriptions: foodTruck.descriptions,
        reseaux_sociaux: foodTruck.reseaux_sociaux,
        cuisine_options: foodTruck.cuisine_options,
      });
      toast.success("Informations enregistrées");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword) {
      toast.error("Veuillez entrer votre mot de passe actuel");
      return;
    }
    if (!passwordData.newPassword) {
      toast.error("Veuillez entrer un nouveau mot de passe");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    setChangingPassword(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success("Mot de passe modifié avec succès");
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors du changement de mot de passe";
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  const handlePhotoUpload = async (category, file) => {
    if (!file) return;
    
    setUploadingPhoto(category);
    try {
      // uploadFile expects a File object, not FormData
      const response = await uploadFile(file);
      const fileId = response.data.file_id;
      
      // Ajouter la photo à la catégorie
      const currentPhotos = foodTruck.photos || {};
      const categoryPhotos = currentPhotos[category] || [];
      
      const updatedPhotos = {
        ...currentPhotos,
        [category]: [...categoryPhotos, { file_id: fileId, filename: file.name }]
      };
      
      setFoodTruck({
        ...foodTruck,
        photos: updatedPhotos
      });
      
      // Sauvegarder immédiatement les photos
      await updateFoodTruck(foodTruck.id, { photos: updatedPhotos });
      
      toast.success("Photo ajoutée");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingPhoto(null);
    }
  };

  const removePhoto = async (category, index) => {
    const currentPhotos = foodTruck.photos || {};
    const categoryPhotos = [...(currentPhotos[category] || [])];
    categoryPhotos.splice(index, 1);
    
    const updatedPhotos = {
      ...currentPhotos,
      [category]: categoryPhotos
    };
    
    setFoodTruck({
      ...foodTruck,
      photos: updatedPhotos
    });
    
    // Sauvegarder immédiatement les modifications
    try {
      await updateFoodTruck(foodTruck.id, { photos: updatedPhotos });
      toast.success("Photo supprimée");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDocumentUpload = async (docType, file, expirationDate) => {
    if (!file) return;
    
    setUploadingDoc(docType);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await uploadFile(formData);
      const fileId = response.data.file_id;
      
      // Mettre à jour le document
      const currentDocs = foodTruck.documents || [];
      const existingIndex = currentDocs.findIndex(d => d.nom === docType || d.type === docType);
      
      const newDoc = {
        nom: docType,
        file_id: fileId,
        filename: file.name,
        date_expiration: expirationDate || null,
        uploaded_at: new Date().toISOString()
      };
      
      let updatedDocs;
      if (existingIndex >= 0) {
        updatedDocs = [...currentDocs];
        updatedDocs[existingIndex] = newDoc;
      } else {
        updatedDocs = [...currentDocs, newDoc];
      }
      
      setFoodTruck({
        ...foodTruck,
        documents: updatedDocs
      });
      
      toast.success("Document mis à jour");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleAddCustomDocument = async () => {
    if (!newDocument.nom || !newDocument.file) {
      toast.error("Veuillez remplir le nom et sélectionner un fichier");
      return;
    }

    setUploadingNewDoc(true);
    try {
      // Upload the file first
      const formData = new FormData();
      formData.append("file", newDocument.file);
      const uploadRes = await uploadFile(formData);
      const fileUrl = `/api/files/${uploadRes.data.file_id}`;

      // Add document via API (this will trigger account blocking for validation)
      const docData = {
        type: newDocument.type || "autre",
        nom: newDocument.nom,
        url: fileUrl,
        date_expiration: newDocument.date_expiration || null,
        description: newDocument.description || null
      };

      const response = await addDocument(foodTruck.id, docData);
      
      // Reload data to get updated status
      await loadData();
      
      setShowAddDocDialog(false);
      setNewDocument({ type: "autre", nom: "", description: "", date_expiration: "", file: null });
      
      if (response.data.account_blocked) {
        toast.warning("Document ajouté. Votre compte est en attente de validation par l'administrateur.", {
          duration: 5000
        });
      } else {
        toast.success("Document ajouté avec succès");
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout du document");
    } finally {
      setUploadingNewDoc(false);
    }
  };

  const getMissingDocuments = () => {
    if (!foodTruck) return [];
    const docs = foodTruck.documents || [];
    return REQUIRED_DOCUMENTS.filter(reqDoc => {
      return !docs.some(d => d.type === reqDoc.id || d.nom?.toLowerCase().includes(reqDoc.label.toLowerCase()));
    });
  };

  const getCustomDocuments = () => {
    if (!foodTruck) return [];
    const docs = foodTruck.documents || [];
    return docs.filter(d => d.type === "autre" || !REQUIRED_DOCUMENTS.find(rd => rd.id === d.type));
  };

  const toggleSpecialty = (spec) => {
    const current = foodTruck.specialites || [];
    if (current.includes(spec)) {
      setFoodTruck({
        ...foodTruck,
        specialites: current.filter(s => s !== spec)
      });
    } else {
      setFoodTruck({
        ...foodTruck,
        specialites: [...current, spec]
      });
    }
  };

  const getDocumentStatus = (docType) => {
    const doc = (foodTruck.documents || []).find(d => d.nom === docType || d.type === docType);
    if (!doc) return { status: "missing", label: "Non fourni" };
    
    if (doc.date_expiration) {
      const expDate = new Date(doc.date_expiration);
      const today = new Date();
      const daysUntil = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
      
      if (daysUntil < 0) return { status: "expired", label: "Expiré", doc };
      if (daysUntil < 30) return { status: "expiring", label: `Expire dans ${daysUntil} jours`, doc };
    }
    
    return { status: "valid", label: "Valide", doc };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!foodTruck) {
    return (
      <div className="text-center py-12">
        <Store className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold text-secondary">Aucun food truck associé</h2>
        <p className="text-muted-foreground">Contactez l'administrateur pour associer votre compte.</p>
      </div>
    );
  }

  const missingDocs = getMissingDocuments();
  const customDocs = getCustomDocuments();

  return (
    <div className="space-y-6" data-testid="mon-food-truck-page">
      {/* Status Alerts */}
      {foodTruck.statut === "en_attente_validation" && (
        <div className="bg-status-warning/10 border border-status-warning/30 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-status-warning mt-0.5" />
          <div>
            <p className="font-semibold text-secondary">Compte en attente de validation</p>
            <p className="text-sm text-muted-foreground">
              {foodTruck.motif_blocage || "Vos modifications sont en cours de vérification par l'administrateur."}
            </p>
          </div>
        </div>
      )}

      {foodTruck.statut === "bloque_documents" && (
        <div className="bg-status-error/10 border border-status-error/30 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-status-error mt-0.5" />
          <div>
            <p className="font-semibold text-secondary">Compte bloqué - Documents manquants</p>
            <p className="text-sm text-muted-foreground">
              Veuillez fournir tous les documents obligatoires pour débloquer votre compte.
            </p>
          </div>
        </div>
      )}

      {foodTruck.statut === "bloque_paiement" && (
        <div className="bg-status-error/10 border border-status-error/30 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-status-error mt-0.5" />
          <div>
            <p className="font-semibold text-secondary">Compte bloqué - Cotisation impayée</p>
            <p className="text-sm text-muted-foreground">
              Veuillez régulariser votre cotisation pour débloquer votre compte.
            </p>
          </div>
        </div>
      )}

      {/* Header with badges */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black text-primary">
                Mon Food Truck
              </h1>
              {cotisationPaid && (
                <Badge className="bg-status-success text-white text-xs">
                  <CreditCard className="w-3 h-3 mr-1" /> À jour
                </Badge>
              )}
              {foodTruck.membre_bureau && (
                <Badge className="bg-highlight text-white text-xs">
                  Bureau
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              Gérez les informations de {foodTruck.nom_food_truck}
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-primary-hover w-full sm:w-auto"
            data-testid="save-food-truck-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Cotisation Status Card - Visible for members (not bureau) */}
      {cotisationStatus && user?.role === "membre" && !foodTruck?.exempt_cotisation && (
        <Card className={`border-2 ${cotisationStatus.payee ? 'border-green-300 bg-green-50/50' : cotisationStatus.bloque ? 'border-red-300 bg-red-50/50' : 'border-amber-300 bg-amber-50/50'}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className={`w-5 h-5 ${cotisationStatus.payee ? 'text-green-600' : cotisationStatus.bloque ? 'text-red-600' : 'text-amber-600'}`} />
              Cotisation {cotisationStatus.annee}
              {cotisationStatus.payee ? (
                <Badge className="bg-green-600 text-white ml-auto">
                  <CheckCircle className="w-3 h-3 mr-1" /> Payée
                </Badge>
              ) : cotisationStatus.bloque ? (
                <Badge className="bg-red-600 text-white ml-auto">
                  <AlertTriangle className="w-3 h-3 mr-1" /> Compte bloqué
                </Badge>
              ) : (
                <Badge className="bg-amber-600 text-white ml-auto">
                  <Calendar className="w-3 h-3 mr-1" /> En attente
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {cotisationStatus.payee ? (
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                <span>Votre cotisation de <strong>{cotisationStatus.montant}€</strong> a été réglée le {cotisationStatus.date_paiement ? new Date(cotisationStatus.date_paiement).toLocaleDateString('fr-FR') : 'N/A'}</span>
              </div>
            ) : (
              <>
                {/* Amount and pay button */}
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div>
                    <p className="text-sm text-gray-600">Montant à régler</p>
                    <p className="text-2xl font-bold text-primary">{cotisationStatus.montant}€</p>
                  </div>
                  <Button 
                    className="bg-primary hover:bg-primary/90"
                    onClick={() => window.location.href = '/mes-paiements'}
                    data-testid="pay-cotisation-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Payer ma cotisation
                  </Button>
                </div>

                {/* Relances status */}
                {cotisationStatus.relances_envoyees > 0 && (
                  <div className={`p-3 rounded-lg ${cotisationStatus.relances_envoyees >= 3 ? 'bg-red-100 border border-red-300' : 'bg-amber-100 border border-amber-300'}`}>
                    <p className={`font-medium ${cotisationStatus.relances_envoyees >= 3 ? 'text-red-800' : 'text-amber-800'}`}>
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      {cotisationStatus.relances_envoyees}/3 relance(s) envoyée(s)
                    </p>
                    {cotisationStatus.date_derniere_relance && (
                      <p className="text-sm mt-1 opacity-80">
                        Dernière relance : {new Date(cotisationStatus.date_derniere_relance).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                )}

                {/* Days until block */}
                {!cotisationStatus.bloque && cotisationStatus.jours_avant_blocage !== null && cotisationStatus.jours_avant_blocage <= 30 && (
                  <div className="p-3 bg-orange-100 border border-orange-300 rounded-lg">
                    <p className="text-orange-800 font-medium">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      {cotisationStatus.jours_avant_blocage > 0 
                        ? `${cotisationStatus.jours_avant_blocage} jour(s) avant blocage du compte`
                        : "Compte bientôt bloqué !"}
                    </p>
                    <p className="text-sm text-orange-700 mt-1">
                      Date de blocage : {cotisationStatus.date_blocage}
                    </p>
                  </div>
                )}

                {/* Blocked message */}
                {cotisationStatus.bloque && (
                  <div className="p-3 bg-red-100 border border-red-300 rounded-lg">
                    <p className="text-red-800 font-medium">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      {cotisationStatus.raison_blocage}
                    </p>
                    <p className="text-sm text-red-700 mt-1">
                      Veuillez régulariser votre situation pour retrouver l'accès complet à la plateforme.
                    </p>
                  </div>
                )}

                {/* Calendar of relances */}
                {!cotisationStatus.bloque && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">Calendrier des relances :</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      {cotisationStatus.calendrier_relances?.map((rel, idx) => (
                        <div 
                          key={idx} 
                          className={`p-2 rounded text-center ${
                            rel.numero === "blocage" 
                              ? 'bg-red-100 text-red-700 border border-red-200' 
                              : cotisationStatus.relances_envoyees >= rel.numero 
                                ? 'bg-gray-200 text-gray-500 line-through' 
                                : 'bg-amber-100 text-amber-700 border border-amber-200'
                          }`}
                        >
                          <p className="font-medium">{rel.label}</p>
                          <p>{rel.date}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#F0EBE6] w-full overflow-x-auto flex flex-nowrap justify-start gap-1 p-1 scrollbar-hide">
          <TabsTrigger value="infos" className="data-[state=active]:bg-primary data-[state=active]:text-white flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <User className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Infos générales</span>
            <span className="sm:hidden">Infos</span>
          </TabsTrigger>
          <TabsTrigger value="equipement" className="data-[state=active]:bg-primary data-[state=active]:text-white flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <Truck className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Équipement</span>
            <span className="sm:hidden">Équip.</span>
          </TabsTrigger>
          <TabsTrigger value="dossier" className="data-[state=active]:bg-primary data-[state=active]:text-white flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <BookOpen className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Dossier client</span>
            <span className="sm:hidden">Dossier</span>
          </TabsTrigger>
          <TabsTrigger value="societe" className="data-[state=active]:bg-primary data-[state=active]:text-white flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <Building2 className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Société</span>
            <span className="sm:hidden">Soc.</span>
          </TabsTrigger>
          <TabsTrigger value="activite" className="data-[state=active]:bg-primary data-[state=active]:text-white flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <Utensils className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Activité</span>
            <span className="sm:hidden">Act.</span>
          </TabsTrigger>
          <TabsTrigger value="photos" className="data-[state=active]:bg-primary data-[state=active]:text-white flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <Camera className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Photos</span>
            <span className="sm:hidden">Photo</span>
          </TabsTrigger>
          <TabsTrigger value="specialites" className="data-[state=active]:bg-primary data-[state=active]:text-white flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <ChefHat className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Spécialités</span>
            <span className="sm:hidden">Spéc.</span>
          </TabsTrigger>
          <TabsTrigger value="documents" className="data-[state=active]:bg-primary data-[state=active]:text-white flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <FileText className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Documents</span>
            <span className="sm:hidden">Docs</span>
          </TabsTrigger>
          <TabsTrigger value="compte" className="data-[state=active]:bg-primary data-[state=active]:text-white flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <Lock className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Compte</span>
            <span className="sm:hidden">Cpte</span>
          </TabsTrigger>
        </TabsList>

        {/* Informations */}
        <TabsContent value="infos">
          <Card className="bg-white border-[#E5E0DC]">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                <Store className="w-5 h-5 text-primary" />
                Informations générales
              </CardTitle>
              <CardDescription>
                Ces informations sont visibles par les administrateurs du collectif
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-secondary font-semibold flex items-center gap-2">
                    <Truck className="w-4 h-4" /> Nom du Food Truck
                  </Label>
                  <Input
                    value={foodTruck.nom_food_truck || ""}
                    onChange={(e) => setFoodTruck({ ...foodTruck, nom_food_truck: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                    data-testid="input-nom-food-truck"
                  />
                </div>
                <div>
                  <Label className="text-secondary font-semibold flex items-center gap-2">
                    <User className="w-4 h-4" /> Nom du contact
                  </Label>
                  <Input
                    value={foodTruck.nom_contact || ""}
                    onChange={(e) => setFoodTruck({ ...foodTruck, nom_contact: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                    data-testid="input-nom-contact"
                  />
                </div>
                <div>
                  <Label className="text-secondary font-semibold flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email
                  </Label>
                  <Input
                    type="email"
                    value={foodTruck.email || ""}
                    onChange={(e) => setFoodTruck({ ...foodTruck, email: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label className="text-secondary font-semibold flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Téléphone
                  </Label>
                  <Input
                    value={foodTruck.telephone || ""}
                    onChange={(e) => setFoodTruck({ ...foodTruck, telephone: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                    data-testid="input-telephone"
                  />
                </div>
              </div>
              <div>
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Adresse
                </Label>
                <Input
                  value={foodTruck.adresse || ""}
                  onChange={(e) => setFoodTruck({ ...foodTruck, adresse: e.target.value })}
                  className="mt-1 border-[#E5E0DC]"
                  placeholder="Adresse complète"
                  data-testid="input-adresse"
                />
              </div>
              <div>
                <Label className="text-secondary font-semibold">Description</Label>
                <Textarea
                  value={foodTruck.description || ""}
                  onChange={(e) => setFoodTruck({ ...foodTruck, description: e.target.value })}
                  className="mt-1 border-[#E5E0DC]"
                  placeholder="Décrivez votre food truck, votre concept, votre histoire..."
                  rows={4}
                  data-testid="input-description"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Équipement & Équipe */}
        <TabsContent value="equipement">
          <div className="space-y-6">
            {/* Véhicule */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Truck className="w-5 h-5 text-primary" />
                  Véhicule
                </CardTitle>
                <CardDescription>
                  Informations sur votre véhicule de restauration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-secondary font-semibold">Type de véhicule</Label>
                    <Select
                      value={foodTruck.type_vehicule || ""}
                      onValueChange={(value) => setFoodTruck({ ...foodTruck, type_vehicule: value })}
                    >
                      <SelectTrigger className="mt-1 border-[#E5E0DC]" data-testid="select-type-vehicule">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Camion">
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4" />
                            Camion
                          </div>
                        </SelectItem>
                        <SelectItem value="Remorque">
                          <div className="flex items-center gap-2">
                            <Car className="w-4 h-4" />
                            Remorque
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold flex items-center gap-2">
                      <Car className="w-4 h-4" /> Plaque d'immatriculation
                    </Label>
                    <Input
                      value={foodTruck.plaque_immatriculation || ""}
                      onChange={(e) => setFoodTruck({ ...foodTruck, plaque_immatriculation: e.target.value.toUpperCase() })}
                      className="mt-1 border-[#E5E0DC] font-mono"
                      placeholder="AA-123-BB"
                      data-testid="input-plaque-immatriculation"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Besoins Électriques */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Besoins Électriques
                </CardTitle>
                <CardDescription>
                  Puissance électrique nécessaire pour votre activité
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg border border-[#E5E0DC] p-4 space-y-3">
                  <Label className="text-secondary font-semibold">Taille du camion (en mètres)</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[["longueur", "Longueur", "Ex: 6,50"], ["largeur", "Largeur", "Ex: 2,30"], ["hauteur", "Hauteur", "Ex: 3,20"]].map(([k, lab, ph]) => (
                      <div key={k}>
                        <Label className="text-sm">{lab} (m)</Label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={foodTruck[k] === null || foodTruck[k] === undefined ? "" : String(foodTruck[k]).replace(".", ",")}
                          onChange={(e) => setFoodTruck({ ...foodTruck, [k]: e.target.value.replace(/[^0-9.,]/g, "") })}
                          className="border-[#E5E0DC] mt-1"
                          placeholder={ph}
                          data-testid={`input-${k}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-md bg-muted/40 p-3">
                      <p className="text-xs text-muted-foreground">Emprise au sol (L x l)</p>
                      <p className="font-semibold text-secondary">{fmt(toNum(foodTruck.longueur) * toNum(foodTruck.largeur), "m²")}</p>
                    </div>
                    <div className="rounded-md bg-muted/40 p-3">
                      <p className="text-xs text-muted-foreground">Volume (L x l x h)</p>
                      <p className="font-semibold text-secondary">{fmt(toNum(foodTruck.longueur) * toNum(foodTruck.largeur) * toNum(foodTruck.hauteur), "m³")}</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-secondary font-semibold">Puissance requise (kW)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        value={foodTruck.puissance_electrique || ""}
                        onChange={(e) => setFoodTruck({ 
                          ...foodTruck, 
                          puissance_electrique: e.target.value ? parseFloat(e.target.value) : null 
                        })}
                        className="border-[#E5E0DC]"
                        placeholder="Ex: 6"
                        data-testid="input-puissance-electrique"
                      />
                      <span className="text-muted-foreground font-medium">kW</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Indiquez la puissance totale nécessaire pour faire fonctionner votre équipement
                    </p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <Zap className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">Aide au calcul</p>
                        <ul className="text-xs text-amber-700 mt-1 space-y-1">
                          <li>• Friteuse : ~3 kW</li>
                          <li>• Plancha : ~3-6 kW</li>
                          <li>• Réfrigérateur : ~0.5 kW</li>
                          <li>• Éclairage : ~0.5 kW</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Équipe */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  Équipe
                </CardTitle>
                <CardDescription>
                  Nombre de personnes habituellement présentes sur les événements
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-secondary font-semibold">Staff de base (personnes)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        min="1"
                        max="20"
                        value={foodTruck.staff_base || ""}
                        onChange={(e) => setFoodTruck({ 
                          ...foodTruck, 
                          staff_base: e.target.value ? parseInt(e.target.value) : null 
                        })}
                        className="border-[#E5E0DC] w-24"
                        placeholder="2"
                        data-testid="input-staff-base"
                      />
                      <span className="text-muted-foreground">personne(s)</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Ce nombre sera pré-rempli lors de votre participation aux événements, mais vous pourrez l'ajuster pour chaque événement
                    </p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <Users className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-800">Bon à savoir</p>
                        <p className="text-xs text-blue-700 mt-1">
                          Lors de votre inscription à un événement, vous pourrez modifier le nombre de personnes présentes si besoin (renfort pour un gros événement, etc.)
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Récapitulatif */}
            {(foodTruck.type_vehicule || foodTruck.plaque_immatriculation || foodTruck.puissance_electrique || foodTruck.staff_base || foodTruck.longueur) && (
              <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-secondary flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-status-success" />
                    Récapitulatif de votre équipement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {foodTruck.type_vehicule && (
                      <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                        <Truck className="w-6 h-6 mx-auto text-primary mb-1" />
                        <p className="text-xs text-muted-foreground">Type</p>
                        <p className="font-semibold text-secondary">{foodTruck.type_vehicule}</p>
                      </div>
                    )}
                    {foodTruck.plaque_immatriculation && (
                      <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                        <Car className="w-6 h-6 mx-auto text-primary mb-1" />
                        <p className="text-xs text-muted-foreground">Plaque</p>
                        <p className="font-semibold text-secondary font-mono">{foodTruck.plaque_immatriculation}</p>
                      </div>
                    )}
                    {foodTruck.puissance_electrique && (
                      <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                        <Zap className="w-6 h-6 mx-auto text-amber-500 mb-1" />
                        <p className="text-xs text-muted-foreground">Électricité</p>
                        <p className="font-semibold text-secondary">{foodTruck.puissance_electrique} kW</p>
                      </div>
                    )}
                    {foodTruck.staff_base && (
                      <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                        <Users className="w-6 h-6 mx-auto text-blue-500 mb-1" />
                        <p className="text-xs text-muted-foreground">Équipe</p>
                        <p className="font-semibold text-secondary">{foodTruck.staff_base} pers.</p>
                      </div>
                    )}
                    {toNum(foodTruck.longueur) && toNum(foodTruck.largeur) && toNum(foodTruck.hauteur) ? (
                      <>
                        <div className="text-center p-3 bg-white rounded-lg shadow-sm" data-testid="recap-dimensions">
                          <Truck className="w-6 h-6 mx-auto text-primary mb-1" />
                          <p className="text-xs text-muted-foreground">Dimensions (L x l x h)</p>
                          <p className="font-semibold text-secondary">{[foodTruck.longueur, foodTruck.largeur, foodTruck.hauteur].map((v) => String(toNum(v)).replace(".", ",")).join(" x ")} m</p>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                          <Truck className="w-6 h-6 mx-auto text-primary mb-1" />
                          <p className="text-xs text-muted-foreground">Emprise au sol</p>
                          <p className="font-semibold text-secondary">{fmt(toNum(foodTruck.longueur) * toNum(foodTruck.largeur), "m²")}</p>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                          <Truck className="w-6 h-6 mx-auto text-primary mb-1" />
                          <p className="text-xs text-muted-foreground">Volume</p>
                          <p className="font-semibold text-secondary">{fmt(toNum(foodTruck.longueur) * toNum(foodTruck.largeur) * toNum(foodTruck.hauteur), "m³")}</p>
                        </div>
                      </>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Société */}
        <TabsContent value="dossier">
          <p className="text-sm text-muted-foreground mb-3">
            Ce qui apparaît dans les dossiers de présentation envoyés aux clients : photos, texte, carte
            et besoins électriques. Les dimensions, le véhicule et le contact viennent des onglets
            Infos générales et Équipement.
          </p>
          <FicheCamionPage integre />
        </TabsContent>

        <TabsContent value="societe">
          <div className="space-y-6">
            {/* Identité de la société */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  Identité de la société
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-secondary font-semibold">SIREN</Label>
                    <Input
                      value={foodTruck.societe?.siren || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        societe: { ...foodTruck.societe, siren: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="123456789"
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">SIRET</Label>
                    <Input
                      value={foodTruck.societe?.siret || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        societe: { ...foodTruck.societe, siret: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="12345678901234"
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Code APE</Label>
                    <Input
                      value={foodTruck.societe?.code_ape || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        societe: { ...foodTruck.societe, code_ape: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="5610C"
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">N° TVA</Label>
                    <Input
                      value={foodTruck.societe?.numero_tva || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        societe: { ...foodTruck.societe, numero_tva: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="FR12345678901"
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Forme juridique</Label>
                    <Input
                      value={foodTruck.societe?.forme_juridique || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        societe: { ...foodTruck.societe, forme_juridique: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="SARL, SAS, Auto-entrepreneur..."
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Nombre de salariés</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={foodTruck.societe?.nbre_salaries || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFoodTruck({ 
                          ...foodTruck, 
                          societe: { ...foodTruck.societe, nbre_salaries: val === "" ? 0 : parseInt(val) || 0 }
                        });
                      }}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="0"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Représentant principal */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Représentant principal
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-secondary font-semibold">Nom complet</Label>
                    <Input
                      value={foodTruck.representant?.nom || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        representant: { ...foodTruck.representant, nom: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Email</Label>
                    <Input
                      type="email"
                      value={foodTruck.representant?.email || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        representant: { ...foodTruck.representant, email: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Téléphone fixe</Label>
                    <Input
                      value={foodTruck.representant?.telephone || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        representant: { ...foodTruck.representant, telephone: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Portable</Label>
                    <Input
                      value={foodTruck.representant?.portable || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        representant: { ...foodTruck.representant, portable: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Préférences de communication */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Bell className="w-5 h-5 text-primary" />
                  Préférences de communication
                </CardTitle>
                <CardDescription>
                  Choisissez les types de communications que vous souhaitez recevoir
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { key: "plannings", label: "Plannings" },
                  { key: "festivals", label: "Festivals" },
                  { key: "demandes", label: "Demandes" },
                  { key: "partenaires", label: "Partenaires" },
                ].map((pref) => (
                  <div key={pref.key} className="flex items-center justify-between p-3 bg-[#F9F7F5] rounded-lg">
                    <span className="font-medium text-secondary">{pref.label}</span>
                    <Button
                      type="button"
                      variant={foodTruck.communications?.[pref.key] ? "default" : "secondary"}
                      size="sm"
                      className={foodTruck.communications?.[pref.key] ? "bg-status-success" : "bg-muted-foreground"}
                      onClick={() => setFoodTruck({
                        ...foodTruck,
                        communications: {
                          ...foodTruck.communications,
                          [pref.key]: !foodTruck.communications?.[pref.key]
                        }
                      })}
                    >
                      {foodTruck.communications?.[pref.key] ? "Oui" : "Non"}
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Réseaux sociaux */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Globe className="w-5 h-5 text-primary" />
                  Réseaux sociaux
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-secondary font-semibold">Site web</Label>
                    <Input
                      value={foodTruck.reseaux_sociaux?.site_web || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        reseaux_sociaux: { ...foodTruck.reseaux_sociaux, site_web: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Facebook</Label>
                    <Input
                      value={foodTruck.reseaux_sociaux?.facebook || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        reseaux_sociaux: { ...foodTruck.reseaux_sociaux, facebook: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="URL ou nom de page"
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Instagram</Label>
                    <Input
                      value={foodTruck.reseaux_sociaux?.instagram || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        reseaux_sociaux: { ...foodTruck.reseaux_sociaux, instagram: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="@votre_compte"
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Twitter</Label>
                    <Input
                      value={foodTruck.reseaux_sociaux?.twitter || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        reseaux_sociaux: { ...foodTruck.reseaux_sociaux, twitter: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="@votre_compte"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Activité */}
        <TabsContent value="activite">
          <div className="space-y-6">
            {/* Point de restauration */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Utensils className="w-5 h-5 text-primary" />
                  Point de restauration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-secondary font-semibold">Nom commercial</Label>
                    <Input
                      value={foodTruck.point_restauration?.nom_commercial || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        point_restauration: { ...foodTruck.point_restauration, nom_commercial: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Date début activité</Label>
                    <Input
                      type="date"
                      value={foodTruck.point_restauration?.date_debut_activite || ""}
                      onChange={(e) => setFoodTruck({ 
                        ...foodTruck, 
                        point_restauration: { ...foodTruck.point_restauration, date_debut_activite: e.target.value }
                      })}
                      className="mt-1 border-[#E5E0DC]"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Descriptions */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Descriptions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-secondary font-semibold">Résumé</Label>
                  <Textarea
                    value={foodTruck.descriptions?.resume || ""}
                    onChange={(e) => setFoodTruck({ 
                      ...foodTruck, 
                      descriptions: { ...foodTruck.descriptions, resume: e.target.value }
                    })}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="Résumé court de votre activité..."
                    rows={2}
                  />
                </div>
                <div>
                  <Label className="text-secondary font-semibold">Concept</Label>
                  <Textarea
                    value={foodTruck.descriptions?.concept || ""}
                    onChange={(e) => setFoodTruck({ 
                      ...foodTruck, 
                      descriptions: { ...foodTruck.descriptions, concept: e.target.value }
                    })}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="Décrivez votre concept culinaire..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label className="text-secondary font-semibold">Description commerciale</Label>
                  <Textarea
                    value={foodTruck.descriptions?.description_commerciale || ""}
                    onChange={(e) => setFoodTruck({ 
                      ...foodTruck, 
                      descriptions: { ...foodTruck.descriptions, description_commerciale: e.target.value }
                    })}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="Description pour les clients..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Options & Services */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Settings className="w-5 h-5 text-primary" />
                  Options & Services
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {[
                    { key: "service_stand", label: "Service en stand" },
                    { key: "electrique_100", label: "100% électrique" },
                  ].map((option) => (
                    <div key={option.key} className="flex items-center justify-between p-3 bg-[#F9F7F5] rounded-lg">
                      <span className="font-medium text-secondary">{option.label}</span>
                      <Button
                        type="button"
                        variant={foodTruck.cuisine_options?.[option.key] ? "default" : "secondary"}
                        size="sm"
                        className={foodTruck.cuisine_options?.[option.key] ? "bg-status-success" : "bg-muted-foreground"}
                        onClick={() => setFoodTruck({
                          ...foodTruck,
                          cuisine_options: {
                            ...foodTruck.cuisine_options,
                            [option.key]: !foodTruck.cuisine_options?.[option.key]
                          }
                        })}
                      >
                        {foodTruck.cuisine_options?.[option.key] ? "Oui" : "Non"}
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-[#E5E0DC]">
                  {[
                    { key: "vegan", label: "Vegan" },
                    { key: "vegetarien", label: "Végétarien" },
                    { key: "halal", label: "Halal" },
                  ].map((option) => (
                    <div key={option.key}>
                      <Label className="text-secondary font-semibold">{option.label}</Label>
                      <Select
                        value={foodTruck.cuisine_options?.[option.key] || "non"}
                        onValueChange={(value) => setFoodTruck({
                          ...foodTruck,
                          cuisine_options: {
                            ...foodTruck.cuisine_options,
                            [option.key]: value
                          }
                        })}
                      >
                        <SelectTrigger className="mt-1 border-[#E5E0DC]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="non">Non</SelectItem>
                          <SelectItem value="oui">Oui</SelectItem>
                          <SelectItem value="sur_demande">Sur demande</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                <div>
                  <Label className="text-secondary font-semibold">Type de cuisine</Label>
                  <Input
                    value={foodTruck.cuisine_options?.type_cuisine || ""}
                    onChange={(e) => setFoodTruck({ 
                      ...foodTruck, 
                      cuisine_options: { ...foodTruck.cuisine_options, type_cuisine: e.target.value }
                    })}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="Ex: Cuisine française, Street food asiatique..."
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Photos */}
        <TabsContent value="photos">
          <div className="space-y-6">
            {PHOTO_CATEGORIES.map((category) => {
              const Icon = category.icon;
              const photos = (foodTruck.photos || {})[category.id] || [];
              
              return (
                <Card key={category.id} className="bg-white border-[#E5E0DC]">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg text-secondary flex items-center gap-2">
                      <Icon className="w-5 h-5 text-primary" />
                      {category.label}
                    </CardTitle>
                    <CardDescription>{category.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {photos.map((photo, index) => (
                        <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-[#F9F7F5] border border-[#E5E0DC]">
                          <img
                            src={`${API_URL}/api/files/${photo.file_id}`}
                            alt={photo.filename}
                            className="w-full h-full object-cover"
                          />
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute top-2 right-2 w-6 h-6"
                            onClick={() => removePhoto(category.id, index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      
                      {/* Bouton d'ajout */}
                      <label 
                        className="aspect-square rounded-lg border-2 border-dashed border-[#E5E0DC] flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                        data-testid={`add-photo-${category.id}`}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handlePhotoUpload(category.id, e.target.files[0])}
                          disabled={uploadingPhoto === category.id}
                          data-testid={`input-photo-${category.id}`}
                        />
                        {uploadingPhoto === category.id ? (
                          <Loader2 className="w-8 h-8 text-primary animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                            <span className="text-sm text-muted-foreground">Ajouter</span>
                          </>
                        )}
                      </label>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Spécialités */}
        <TabsContent value="specialites">
          <Card className="bg-white border-[#E5E0DC]">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                <ChefHat className="w-5 h-5 text-primary" />
                Vos spécialités
              </CardTitle>
              <CardDescription>
                Sélectionnez les spécialités qui correspondent à votre cuisine
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <p className="text-sm text-muted-foreground">
                  {(foodTruck.specialites || []).length} spécialité(s) sélectionnée(s)
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(foodTruck.specialites || []).map((spec) => (
                    <Badge
                      key={spec}
                      className="bg-primary text-white cursor-pointer"
                      onClick={() => toggleSpecialty(spec)}
                    >
                      {spec}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="border-t border-[#E5E0DC] pt-4">
                <p className="text-sm font-semibold text-secondary mb-3">Toutes les spécialités disponibles :</p>
                <div className="flex flex-wrap gap-2 max-h-96 overflow-y-auto">
                  {specialties.map((spec) => {
                    const isSelected = (foodTruck.specialites || []).includes(spec);
                    return (
                      <Badge
                        key={spec}
                        variant={isSelected ? "default" : "outline"}
                        className={`cursor-pointer transition-all ${
                          isSelected
                            ? "bg-primary text-white"
                            : "border-[#E5E0DC] hover:border-primary hover:text-primary"
                        }`}
                        onClick={() => toggleSpecialty(spec)}
                      >
                        {spec}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents">
          <div className="space-y-6">
            {/* Alerte documents manquants */}
            {missingDocs.length > 0 && (
              <div className="bg-status-error/10 border border-status-error/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-status-error mt-0.5" />
                  <div>
                    <p className="font-semibold text-secondary">Documents obligatoires manquants</p>
                    <p className="text-sm text-muted-foreground">
                      Veuillez fournir les documents suivants : {missingDocs.map(d => d.label).join(", ")}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Documents obligatoires */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Documents obligatoires
                </CardTitle>
                <CardDescription>
                  Maintenez vos documents à jour pour rester en conformité
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {REQUIRED_DOCUMENTS.map((docType) => {
                  const { status, label, doc } = getDocumentStatus(docType.id);
                  
                  return (
                    <div
                      key={docType.id}
                      className={`p-4 rounded-lg border ${
                        status === "expired"
                          ? "border-status-error bg-status-error/5"
                          : status === "expiring"
                            ? "border-status-warning bg-status-warning/5"
                            : status === "valid"
                              ? "border-status-success bg-status-success/5"
                              : "border-status-error/50 bg-status-error/5"
                      }`}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                          {status === "valid" && <CheckCircle className="w-5 h-5 text-status-success" />}
                          {status === "expiring" && <AlertTriangle className="w-5 h-5 text-status-warning" />}
                          {status === "expired" && <AlertTriangle className="w-5 h-5 text-status-error" />}
                          {status === "missing" && <AlertTriangle className="w-5 h-5 text-status-error" />}
                          
                          <div>
                            <p className="font-semibold text-secondary">
                              {docType.label}
                              {docType.required && <span className="text-status-error ml-1">*</span>}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {doc ? doc.filename || "Document fourni" : "Non fourni - Obligatoire"}
                            </p>
                            {doc?.date_expiration && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                <Calendar className="w-3 h-3" />
                                Expire le {new Date(doc.date_expiration).toLocaleDateString("fr-FR")}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {doc && doc.url && (
                            <a
                              href={doc.url}
                              onClick={(e) => { e.preventDefault(); ouvrirFichier(doc.url).catch(() => toast.error("Document indisponible ou accès refusé")); }}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded text-sm hover:bg-primary/20"
                            >
                              <Download className="w-4 h-4" /> Télécharger
                            </a>
                          )}
                          
                          <div className="flex items-center gap-2">
                            {docType.hasExpiration && (
                              <Input
                                type="date"
                                className="w-40 border-[#E5E0DC]"
                                placeholder="Date d'expiration"
                                id={`exp-${docType.id}`}
                              />
                            )}
                            <label>
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const expInput = document.getElementById(`exp-${docType.id}`);
                                  handleDocumentUpload(docType.id, e.target.files[0], expInput?.value);
                                }}
                                disabled={uploadingDoc === docType.id}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-[#E5E0DC]"
                                disabled={uploadingDoc === docType.id}
                                asChild
                              >
                                <span>
                                  {uploadingDoc === docType.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <><Upload className="w-4 h-4 mr-1" /> {doc ? "Remplacer" : "Ajouter"}</>
                                  )}
                                </span>
                              </Button>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Documents supplémentaires */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                      <Plus className="w-5 h-5 text-primary" />
                      Documents supplémentaires
                    </CardTitle>
                    <CardDescription>
                      Ajoutez d'autres documents si nécessaire (certificats, diplômes, etc.)
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => setShowAddDocDialog(true)}
                    className="bg-primary hover:bg-primary-hover"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un document
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {customDocs.length > 0 ? (
                  <div className="space-y-3">
                    {customDocs.map((doc, index) => (
                      <div key={doc.id || index} className="p-4 rounded-lg border border-[#E5E0DC] bg-[#F9F7F5]">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-primary" />
                            <div>
                              <p className="font-semibold text-secondary">{doc.nom}</p>
                              {doc.description && (
                                <p className="text-sm text-muted-foreground">{doc.description}</p>
                              )}
                              {doc.date_expiration && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                  <Calendar className="w-3 h-3" />
                                  Expire le {new Date(doc.date_expiration).toLocaleDateString("fr-FR")}
                                </p>
                              )}
                            </div>
                          </div>
                          {doc.url && (
                            <a
                              href={doc.url}
                              onClick={(e) => { e.preventDefault(); ouvrirFichier(doc.url).catch(() => toast.error("Document indisponible ou accès refusé")); }}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded text-sm hover:bg-primary/20"
                            >
                              <Download className="w-4 h-4" /> Télécharger
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun document supplémentaire</p>
                    <p className="text-sm">Cliquez sur "Ajouter un document" pour en ajouter</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <p className="text-sm text-muted-foreground text-center">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              Note : L'ajout ou la modification de documents nécessite une validation par l'administrateur.
            </p>
          </div>
        </TabsContent>

        {/* Compte */}
        <TabsContent value="compte">
          <Card className="bg-white border-[#E5E0DC]">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                Changer le mot de passe
              </CardTitle>
              <CardDescription>
                Modifiez votre mot de passe de connexion
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 max-w-md">
              <div>
                <Label className="text-secondary font-semibold">Mot de passe actuel</Label>
                <div className="relative mt-1">
                  <Input
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    className="border-[#E5E0DC] pr-10"
                    placeholder="••••••••"
                    data-testid="member-current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-secondary"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label className="text-secondary font-semibold">Nouveau mot de passe</Label>
                <div className="relative mt-1">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    className="border-[#E5E0DC] pr-10"
                    placeholder="••••••••"
                    data-testid="member-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-secondary"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Minimum 6 caractères</p>
              </div>
              
              <div>
                <Label className="text-secondary font-semibold">Confirmer le nouveau mot de passe</Label>
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="mt-1 border-[#E5E0DC]"
                  placeholder="••••••••"
                  data-testid="member-confirm-password"
                />
              </div>
              
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="bg-primary hover:bg-primary-hover"
                data-testid="member-change-password-btn"
              >
                {changingPassword ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Lock className="w-4 h-4 mr-2" />
                )}
                Changer le mot de passe
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog pour ajouter un document */}
      <Dialog open={showAddDocDialog} onOpenChange={setShowAddDocDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-secondary flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Ajouter un document
            </DialogTitle>
            <DialogDescription>
              Ajoutez un document supplémentaire à votre dossier
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-secondary font-semibold">Nom du document *</Label>
              <Input
                value={newDocument.nom}
                onChange={(e) => setNewDocument({ ...newDocument, nom: e.target.value })}
                placeholder="Ex: Certificat de formation, Diplôme..."
                className="mt-1 border-[#E5E0DC]"
              />
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Description (optionnel)</Label>
              <Input
                value={newDocument.description}
                onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
                placeholder="Précisez de quel document il s'agit"
                className="mt-1 border-[#E5E0DC]"
              />
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Date d'expiration (optionnel)</Label>
              <Input
                type="date"
                value={newDocument.date_expiration}
                onChange={(e) => setNewDocument({ ...newDocument, date_expiration: e.target.value })}
                className="mt-1 border-[#E5E0DC]"
              />
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Fichier *</Label>
              <div className="mt-1">
                {newDocument.file ? (
                  <div className="flex items-center gap-2 p-3 bg-status-success/10 border border-status-success/30 rounded-lg">
                    <FileText className="w-5 h-5 text-status-success" />
                    <span className="flex-1 text-sm text-secondary truncate">{newDocument.file.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setNewDocument({ ...newDocument, file: null })}
                      className="text-status-error hover:text-status-error/80"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-[#E5E0DC] rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                    <Upload className="w-5 h-5 text-primary" />
                    <span className="text-sm text-muted-foreground">Cliquez pour sélectionner un fichier</span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => setNewDocument({ ...newDocument, file: e.target.files[0] })}
                    />
                  </label>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">PDF, Image ou Document (max 10MB)</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddDocDialog(false);
                setNewDocument({ type: "autre", nom: "", description: "", date_expiration: "", file: null });
              }}
              className="border-[#E5E0DC]"
            >
              Annuler
            </Button>
            <Button
              onClick={handleAddCustomDocument}
              disabled={uploadingNewDoc}
              className="bg-primary hover:bg-primary-hover"
            >
              {uploadingNewDoc ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Upload...</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" /> Ajouter</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
